import { useId, useRef, useState } from 'react';

import { Button, Icon, IconButton } from '@/components/ui';
import { useRealtime } from '@/features/realtime/useRealtime';
import { formatFileSize } from '@/utils/format';

const MAX_LENGTH = 4000;

/**
 * Writing a message, and attaching a file to one.
 *
 * The typing signal is sent from here and throttled in the provider, so a fast
 * typist produces one frame every few seconds rather than one per keystroke.
 *
 * Enter sends and Shift+Enter breaks the line, which is what everybody's hands
 * already expect. The send button stays for anyone whose hands do not.
 */
export function Composer({ conversationId, onSend, onAttach, isSending }) {
  const [body, setBody] = useState('');
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);
  const inputId = useId();
  const { sendTyping } = useRealtime();

  function reset() {
    setBody('');
    setFile(null);
    if (fileRef.current) fileRef.current.value = '';
  }

  async function submit(event) {
    event?.preventDefault();
    const text = body.trim();
    if (!text && !file) return;
    if (file) {
      await onAttach(file, text);
    } else {
      await onSend(text);
    }
    reset();
  }

  function removeFile() {
    setFile(null);
    if (fileRef.current) fileRef.current.value = '';
  }

  return (
    <form className="pt-3" onSubmit={submit}>
      <div className="border-line bg-surface focus-within:border-brand-400 focus-within:ring-brand-100 rounded-card border shadow-xs transition focus-within:ring-4">
        {file && (
          <div className="px-3 pt-3">
            <span className="bg-brand-50 text-ink border-brand-100 inline-flex max-w-full items-center gap-2 rounded-lg border py-1.5 pr-1.5 pl-2.5 text-xs">
              <Icon name="document" size={15} className="text-brand-600 shrink-0" />
              <span className="truncate font-medium">{file.name}</span>
              <span className="text-ink-muted shrink-0">{formatFileSize(file.size)}</span>
              <button
                type="button"
                onClick={removeFile}
                aria-label="Remove attachment"
                className="text-ink-muted hover:bg-brand-100 hover:text-ink grid size-5 shrink-0 place-items-center rounded transition"
              >
                <Icon name="close" size={12} />
              </button>
            </span>
          </div>
        )}

        <label htmlFor={inputId} className="sr-only">
          Message
        </label>
        <textarea
          id={inputId}
          rows={2}
          value={body}
          maxLength={MAX_LENGTH}
          placeholder={file ? 'Add a note about this file (optional)' : 'Write a message…'}
          className="text-ink placeholder:text-ink-muted/70 block w-full resize-none bg-transparent px-4 pt-3 pb-1 text-sm leading-6 outline-none"
          onChange={(event) => {
            setBody(event.target.value);
            if (conversationId) sendTyping(conversationId);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              void submit();
            }
          }}
        />

        <div className="flex items-center justify-between gap-3 px-2 pb-2">
          <div className="flex items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
              className="hidden"
              onChange={(event) => setFile(event.target.files?.[0] ?? null)}
            />
            <IconButton
              icon="attach"
              label="Attach a file"
              onClick={() => fileRef.current?.click()}
            />
            <span className="text-ink-muted hidden text-[11px] sm:inline">
              <kbd className="font-sans font-semibold">Enter</kbd> to send ·{' '}
              <kbd className="font-sans font-semibold">Shift + Enter</kbd> for a new line
            </span>
          </div>
          <Button type="submit" size="sm" isLoading={isSending} disabled={!body.trim() && !file}>
            {file ? 'Send file' : 'Send'}
            <Icon name="send" size={15} />
          </Button>
        </div>
      </div>
    </form>
  );
}
